import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-foreground-400 text-2xl w-8 h-8 flex items-center justify-center" />
          </div>
          <p className="text-foreground-600 mb-4">文章不存在或已被移除</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm font-medium whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category?.name || '分类', href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '喵绅士',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://yufangwlkk.cn/article/${article.slug}`,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://yufangwlkk.cn', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': `https://yufangwlkk.cn/category/${article.category}`, name: category?.name || '' } },
      { '@type': 'ListItem', position: 3, item: { '@id': `https://yufangwlkk.cn/article/${article.slug}`, name: article.title } },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <main className="min-h-screen pt-16">
        <article>
          <div className="bg-background-100 border-b border-background-200/60">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <div className="mb-4">
                <Breadcrumb items={breadcrumbItems} />
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-3">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">{article.author}</p>
                  <p className="text-xs text-foreground-500 whitespace-nowrap">
                    {article.date} · {article.readTime} 分钟阅读
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="mb-10 rounded-lg overflow-hidden aspect-[16/9]">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-semibold
                prose-p:text-foreground-700 prose-p:leading-relaxed
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-strong:text-foreground-900
                [&_h2]:font-heading [&_h2]:text-foreground-950 [&_h2]:font-semibold [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-4
                [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:text-base md:[&_p]:text-lg [&_p]:mb-5
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-16 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h4 className="text-lg md:text-xl font-heading font-semibold text-foreground-950 mb-6">
                <a href="#related" className="no-underline text-foreground-950">相关文章</a>
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.slug} article={rel} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}